import React, { useEffect, useState } from "react";
import { X, AlertCircle } from "lucide-react";
import { Upload } from "../AdminIcons";
import { useForm } from "react-hook-form";
import {
  useCreateCategoryMutation,
  useGetAllAgeGroupsQuery,
  useGetAllLevelsQuery,
  useGetAllSubCategoriesQuery,
  useUpdateCategoryMutation,
} from "../../services/categories/categoriesApi";
import useClickOutsideModal from "../common/useClickOutsideModal";
import { showToast } from "../../utils/toast";

const MultiSelect = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
  isLoading,
  isError,
  error,
  onAddOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectOption = (option) => {
    if (!selected.some((item) => item.value === option.value)) {
      onChange([...selected, option]);
    }
    setIsOpen(false);
  };

  const removeOption = (option) => {
    onChange(selected.filter((item) => item !== option));
  };

  return (
    <div className="relative">
      <p className="mb-2 text-sm font-medium leading-5 text-[#374151]">
        {label}
      </p>

      {/* Select input */}
      <button
        type="button"
        disabled={isLoading || isError}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-[38px] w-full items-center justify-between rounded-lg border border-[#D1D5DB] bg-white px-3 text-left text-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-50"
      >
        <span
          className={`truncate ${
            isLoading || isError ? "text-[#9CA3AF]" : "text-[#9CA3AF]"
          }`}
        >
          {isLoading
            ? "Loading..."
            : isError
              ? "Failed to load options"
              : placeholder}
        </span>

        {!isLoading && !isError && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`shrink-0 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && !isLoading && !isError && (
        <div className="absolute left-0 right-0 top-[62px] z-30 max-h-48 overflow-y-auto rounded-lg border border-[#D1D5DB] bg-white py-1 shadow-lg">
          {options.length > 0 ? (
            options.map((option) => {
              const isSelected = selected.some(
                (item) => item.value === option.value,
              );
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isSelected}
                  onClick={() => selectOption(option)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                    isSelected
                      ? "cursor-not-allowed bg-gray-50 text-gray-400"
                      : "text-[#374151] hover:bg-[#F3E8F2] hover:text-[#923D8C]"
                  }`}
                >
                  <span>{option.label}</span>

                  {isSelected && (
                    <span className="text-xs text-[#923D8C]">Selected</span>
                  )}
                </button>
              );
            })
          ) : (
            <div className="flex flex-col items-center px-3 py-4">
              <p className="mb-2 text-xs text-[#9CA3AF]">No options found</p>
              {onAddOption && (
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onAddOption();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#923D8C] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[#7F3479]"
                >
                  <span className="text-sm leading-none">+</span>
                  Add option
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {isError && (
        <p className="mt-2 text-xs text-red-500">
          {error?.data?.message || "Failed to load options."}
        </p>
      )}

      {/* Selected values */}
      {selected.length > 0 && (
        <div className="mt-[14px] flex flex-wrap gap-1">
          {selected.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => removeOption(item)}
              className="inline-flex items-center gap-2 rounded-full bg-[#DBEAFE] px-2 py-1 text-[11px] leading-4 text-[#1E40AF]"
            >
              <span>{item.label}</span>

              <X size={11} className="text-[#9CA3AF]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
const AddCategoryModal = ({
  isOpen,
  onClose,
  onAddOption,
  category = null,
}) => {
  const [visibility, setVisibility] = useState("active");
  const [categoryIconName, setCategoryIconName] = useState(
    "Upload category icon",
  );

  const [subCategories, setSubCategories] = useState([]);
  const [courseLevels, setCourseLevels] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [existingIconUrl, setExistingIconUrl] = useState("");
  const [apiError, setApiError] = useState("");
  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      categoryName: "",
      categoryDescription: "",
    },
  });

  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoryMutation();

  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();

  const {
    data,
    isLoading: isSubCategoriesLoading,
    isError: isSubCategoriesError,
    error: subCategoriesError,
  } = useGetAllSubCategoriesQuery();

  const {
    data: ageGroupsData,
    isLoading: isAgeGroupsLoading,
    isError: isAgeGroupsError,
    error: ageGroupsError,
  } = useGetAllAgeGroupsQuery();

  const {
    data: levelsData,
    isLoading: isLevelsLoading,
    isError: isLevelsError,
    error: levelsError,
  } = useGetAllLevelsQuery();

  const subCategoryOptions = (data?.data || []).map((sub) => ({
    label: sub.title,
    value: sub._id,
  }));

  const ageGroupOptions = (ageGroupsData?.data || []).map((ageGroup) => ({
    label: ageGroup.title,
    value: ageGroup._id,
  }));

  const levelOptions = (levelsData?.data || []).map((level) => ({
    label: level.title,
    value: level._id,
  }));

  useEffect(() => {
    if (!isOpen) return;

    setApiError("");
    setProfilePictureFile(null);
    clearErrors("categoryIcon");

    if (category) {
      reset({
        categoryName: category.categoryName || "",
        categoryDescription: category.categoryDescription || "",
      });

      setVisibility(category.visibility || "active");

      const existingSubCategories = (category.subCategory || []).map(
        (item) => ({
          label: item.title || item.name || item.label || "",
          value: item._id || item.value || item,
        }),
      );

      const existingCourseLevels = (category.categoryLevel || []).map(
        (item) => ({
          label: item.title || item.name || item.label || "",
          value: item._id || item.value || item,
        }),
      );

      const existingAgeGroups = (category.categoryAgeGroup || []).map(
        (item) => ({
          label: item.title || item.name || item.label || "",
          value: item._id || item.value || item,
        }),
      );

      setSubCategories(existingSubCategories);
      setCourseLevels(existingCourseLevels);
      setAgeGroups(existingAgeGroups);

      const iconUrl = category.icon?.secureUrl || "";
      setExistingIconUrl(iconUrl);
      setCategoryIconName(
        iconUrl ? "Current category icon" : "Upload category icon",
      );
    } else {
      reset({
        categoryName: "",
        categoryDescription: "",
      });

      setVisibility("active");
      setSubCategories([]);
      setCourseLevels([]);
      setAgeGroups([]);
      setProfilePictureFile(null);
      setExistingIconUrl("");
      setCategoryIconName("Upload category icon");
    }
  }, [category, isOpen, reset, clearErrors]);

  const modalRef = useClickOutsideModal(onClose);
  if (!isOpen) {
    return null;
  }

  const onSubmit = async (data) => {
    setApiError("");

    if (!profilePictureFile && !existingIconUrl) {
      setError("categoryIcon", {
        type: "required",
        message: "Category icon is required.",
      });
      return;
    }

    try {
      const formData = new FormData();

      formData.append("categoryName", data.categoryName);
      formData.append("categoryDescription", data.categoryDescription);
      formData.append("visibility", visibility);

      subCategories.forEach((item) => {
        formData.append("subCategory[]", item.value);
      });

      courseLevels.forEach((item) => {
        formData.append("categoryLevel[]", item.value);
      });

      ageGroups.forEach((item) => {
        formData.append("categoryAgeGroup[]", item.value);
      });

      if (profilePictureFile) {
        formData.append("profilePicture", profilePictureFile);
      }

      let response;

      if (category !== null) {
       
        response = await updateCategory({
          id: category._id,
          payload: formData,
        }).unwrap();
      } else {
        response = await createCategory(formData).unwrap();
      }

      showToast({
        message:
          response?.message ||
          (category !== null
            ? "Category updated successfully."
            : "Category created successfully."),
        type: "success",
      });
      reset();
      setSubCategories([]);
      setCourseLevels([]);
      setAgeGroups([]);
      setProfilePictureFile(null);
      setExistingIconUrl("");
      setCategoryIconName("Upload category icon");
      onClose();
    } catch (err) {
      console.error(err);

      setApiError(
        err?.data?.message || "Something went wrong. Please try again.",
      );
    }
  };
  const isSubmitting = isCreating || isUpdating;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-6 sm:p-10">
      <div
        ref={modalRef}
        className="max-h-[min(94vh,calc(100dvh-1.5rem))] w-full max-w-[672px] overflow-hidden rounded-lg bg-[#fff]"
      >
        <div className="flex items-center justify-between gap-4 border-b border-[#E5E7EB] p-4 sm:p-6">
          <h3 className="text-lg font-semibold leading-7 text-[#111827] sm:text-xl">
            {category !== null ? "Update Category" : "Add New Category"}
          </h3>
          <button
            type="button"
            aria-label="Close add category modal"
            onClick={onClose}
            className="rounded-md p-1 text-[#9CA3AF] transition hover:bg-black/5"
          >
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[calc(100dvh-8rem)] overflow-y-auto px-4 pb-6 pt-5 sm:px-6 sm:pb-9">
          <form className="w-full sm:w-auto" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8">
              <div>
                <div className="flex items-center gap-1">
                  <p className="mb-2 text-sm font-medium leading-5 text-[#374151]">
                    Category Name
                  </p>
                  <span className="text-red-500">*</span>
                </div>
                <input
                  type="text"
                  {...register("categoryName", {
                    required: "Category name is required.",
                    minLength: {
                      value: 3,
                      message:
                        "Category name must be between 3 and 100 characters.",
                    },
                    maxLength: {
                      value: 100,
                      message:
                        "Category name must be between 3 and 100 characters.",
                    },
                  })}
                  placeholder="Enter category name"
                  className={`h-[38px] w-full rounded-lg border px-3 text-sm outline-none placeholder:text-[#9CA3AF] ${
                    errors.categoryName ? "border-red-500" : "border-[#D1D5DB]"
                  }`}
                />
                {errors.categoryName && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.categoryName.message}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-1">
                  <p className="mb-2 text-sm font-medium leading-5 text-[#374151]">
                    Category Icon
                  </p>
                  <span className="text-red-500">*</span>
                </div>
                <label
                  className={`flex h-[38px] cursor-pointer items-center justify-between rounded-lg border px-3 text-sm leading-none text-[#9CA3AF] ${
                    errors.categoryIcon
                      ? "border-red-500"
                      : "border-[#D1D5DB]"
                  }`}
                >
                  <span className="truncate pr-3">{categoryIconName}</span>
                  <span className="pointer-events-none text-[#111827]">
                    <Upload />
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      setProfilePictureFile(file || null);
                      setCategoryIconName(
                        file
                          ? file.name
                          : existingIconUrl
                            ? "Current category icon"
                            : "Upload category icon",
                      );
                      if (file || existingIconUrl) {
                        clearErrors("categoryIcon");
                      }
                    }}
                  />
                </label>
                {errors.categoryIcon && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.categoryIcon.message}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-[18px]">
              <div className="flex items-center gap-1">
                <p className=" mb-2 text-sm font-medium leading-5 text-[#374151]">
                  Description
                </p>
                <span className="text-red-500">*</span>
              </div>
              <textarea
                {...register("categoryDescription", {
                  required: "Category description is required.",
                })}
                placeholder="Enter category description"
                className={`h-[78px] w-full resize-none rounded-lg border px-3 py-3 text-sm leading-none outline-none placeholder:text-[#9CA3AF] ${
                  errors.categoryDescription
                    ? "border-red-500"
                    : "border-[#D1D5DB]"
                }`}
              />
              {errors.categoryDescription && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.categoryDescription.message}
                </p>
              )}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <MultiSelect
                label="Sub Category"
                options={subCategoryOptions}
                selected={subCategories}
                onChange={setSubCategories}
                placeholder="Select sub categories"
                isLoading={isSubCategoriesLoading}
                isError={isSubCategoriesError}
                error={subCategoriesError}
                onAddOption={() => onAddOption("subCategory")}
              />

              <MultiSelect
                label="Course Level"
                options={levelOptions}
                selected={courseLevels}
                onChange={setCourseLevels}
                placeholder="Select course levels"
                isLoading={isLevelsLoading}
                isError={isLevelsError}
                error={levelsError}
                onAddOption={() => onAddOption("ageGroup")}
              />

              <MultiSelect
                label="Age Group"
                options={ageGroupOptions}
                selected={ageGroups}
                onChange={setAgeGroups}
                placeholder="Select age groups"
                isLoading={isAgeGroupsLoading}
                isError={isAgeGroupsError}
                error={ageGroupsError}
                onAddOption={() => onAddOption("courseLevel")}
              />
            </div>

            <div className="pb-8 text-sm leading-5 text-[#374151] sm:pb-10">
              <p className="mt-[14px] mb-2  font-medium  ">Visibility</p>
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="category-visibility"
                    checked={visibility === "active"}
                    onChange={() => setVisibility("active")}
                    className="accent-[#923D8C]"
                  />
                  <span>Active</span>
                </label>

                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="category-visibility"
                    checked={visibility === "inactive"}
                    onChange={() => setVisibility("inactive")}
                    className="accent-[#923D8C]"
                  />
                  <span>Inactive</span>
                </label>
              </div>
            </div>

            {apiError && (
              <div className=" flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                <AlertCircle size={16} />
                <span>{apiError}</span>
              </div>
            )}

            <div className="border-t border-[#E5E7EB] pt-6">
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="rounded-lg bg-[#F3F4F6] px-4 py-2 leading-6 text-[#374151]"
                >
                  Cancel
                </button>

                <button
                  disabled={isSubmitting}
                  className="w-full rounded-lg bg-[#923D8C] px-6 py-2 leading-6 text-white disabled:opacity-50 sm:w-auto"
                >
                  {isCreating
                    ? "Creating..."
                    : isUpdating
                      ? "Updating..."
                      : category !== null
                        ? "Update Category"
                        : "Create Category"}
                </button>
              </div>
            </div>
          </form>
          {/* da */}
        </div>
      </div>
    </div>
  );
};

export default AddCategoryModal;
